import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  ArrowLeft, ExternalLink, Save, Clock, X,
  RotateCcw, ChevronDown, Monitor, Tablet, Smartphone,
  Code,
} from 'lucide-react';

// ── Version history helpers (mirrors GrapesJSEditor) ─────────────
const vk = slug => `msb_v_${slug}`;
function pushVersion(slug, html) {
  if (!slug) return;
  try {
    const arr = JSON.parse(localStorage.getItem(vk(slug)) || '[]');
    arr.unshift({ ts: Date.now(), html, id: Math.random().toString(36).slice(2) });
    localStorage.setItem(vk(slug), JSON.stringify(arr.slice(0, 20)));
  } catch (_) {}
}
function getVersions(slug) {
  if (!slug) return [];
  try { return JSON.parse(localStorage.getItem(vk(slug)) || '[]'); }
  catch (_) { return []; }
}
function fmtDate(ts) {
  return new Date(ts).toLocaleString('id-ID', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Styles ────────────────────────────────────────────────────────
const PE_CSS = `
  .pe-wrap { display:flex; flex-direction:column; height:100%; background:#1a1a2e; overflow:hidden; }
  .pe-top  { height:52px; background:#16213e; border-bottom:1px solid rgba(255,255,255,0.08); display:flex; align-items:center; padding:0 12px; gap:8px; flex-shrink:0; }

  /* Tab bar */
  .pe-tabs { display:flex; align-items:center; height:38px; background:#0f172a; border-bottom:1px solid rgba(255,255,255,0.07); padding:0 12px; gap:4px; flex-shrink:0; }
  .pe-tab  { display:flex; align-items:center; gap:6px; padding:5px 14px; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; border:none; color:rgba(255,255,255,0.4); background:none; transition:all 0.13s; }
  .pe-tab:hover { color:rgba(255,255,255,0.75); background:rgba(255,255,255,0.05); }
  .pe-tab.on { background:rgba(251,191,36,0.12); color:#fbbf24; }
  .pe-tab-sep  { width:1px; height:16px; background:rgba(255,255,255,0.1); margin:0 2px; flex-shrink:0; }
  .pe-tab-hint { font-size:10px; color:rgba(255,255,255,0.2); margin-left:10px; font-weight:400; font-family:inherit; }

  /* Content area */
  .pe-content { flex:1; overflow:hidden; min-height:0; display:flex; }

  /* ── HTML Edit pane ──────────────────────────────────────────── */
  .pe-html { display:flex; width:100%; height:100%; }

  .pe-code-col    { width:44%; display:flex; flex-direction:column; border-right:1px solid rgba(255,255,255,0.07); flex-shrink:0; }
  .pe-preview-col { flex:1; display:flex; flex-direction:column; min-width:0; }

  .pe-col-head {
    height:32px; padding:0 14px;
    font-size:10px; font-weight:700; color:rgba(255,255,255,0.28);
    text-transform:uppercase; letter-spacing:1px;
    background:#0d1117; border-bottom:1px solid rgba(255,255,255,0.06);
    display:flex; align-items:center; justify-content:space-between; flex-shrink:0;
  }
  .pe-col-head span:last-child { font-weight:400; color:rgba(255,255,255,0.18); }

  .pe-code-ta {
    flex:1; font-family:'DM Mono',monospace; font-size:12px; line-height:1.65;
    border:none; outline:none; resize:none;
    background:#0d1117; color:#e6edf3;
    padding:14px 16px; tab-size:2;
  }
  .pe-code-ta::selection { background:rgba(251,191,36,0.25); }

  .pe-preview-wrap {
    flex:1; overflow:auto; background:#d1d5db;
    display:flex; align-items:flex-start; justify-content:center;
    padding:16px;
  }
  .pe-preview-iframe {
    background:white; border:none; flex-shrink:0;
    box-shadow:0 4px 32px rgba(0,0,0,0.22);
    transition:width 0.25s ease, height 0.25s ease;
  }

  /* ── Shared top-bar components (msb-*) ──────────────────────── */
  .msb-btn { display:inline-flex; align-items:center; gap:5px; padding:5px 10px; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; white-space:nowrap; transition:all 0.13s; }
  .msb-btn-ghost { background:rgba(255,255,255,0.07); color:rgba(255,255,255,0.75); border:1px solid rgba(255,255,255,0.1); }
  .msb-btn-ghost:hover { background:rgba(255,255,255,0.14); color:white; }
  .msb-btn-ghost:disabled { opacity:0.4; cursor:not-allowed; }
  .msb-btn-save { background:rgba(255,255,255,0.1); color:rgba(255,255,255,0.9); border:1px solid rgba(255,255,255,0.15); }
  .msb-btn-save:hover:not(:disabled) { background:rgba(255,255,255,0.18); }
  .msb-btn-save:disabled { opacity:0.4; cursor:not-allowed; }
  .msb-btn-pub { background:#fbbf24; color:#1e293b; border:none; }
  .msb-btn-pub:hover:not(:disabled) { background:#f59e0b; }
  .msb-btn-pub:disabled { opacity:0.5; cursor:not-allowed; }
  .msb-btn-unpub { background:rgba(245,158,11,0.15); color:#f59e0b; border:1px solid rgba(245,158,11,0.3); }
  .msb-btn-unpub:hover { background:rgba(245,158,11,0.25); }
  .msb-div { width:1px; height:22px; background:rgba(255,255,255,0.1); flex-shrink:0; }
  .msb-ibt { width:30px; height:30px; border-radius:5px; border:none; background:rgba(255,255,255,0.06); color:rgba(255,255,255,0.55); display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.13s; flex-shrink:0; }
  .msb-ibt:hover { background:rgba(255,255,255,0.13); color:white; }
  .msb-ibt.on { background:rgba(255,255,255,0.17); color:white; }
  .msb-dot-live { width:7px; height:7px; border-radius:50%; background:#22c55e; box-shadow:0 0 5px #22c55e; }
  .msb-dot-draft { width:7px; height:7px; border-radius:50%; background:#f59e0b; }
  .msb-dot-unsaved { width:7px; height:7px; border-radius:50%; background:#fbbf24; }
  .msb-spacer { flex:1; }
  .msb-psel { display:flex; align-items:center; gap:6px; background:rgba(255,255,255,0.07); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:5px 10px; cursor:pointer; font-size:12px; font-weight:600; color:rgba(255,255,255,0.9); transition:background 0.13s; max-width:150px; overflow:hidden; }
  .msb-psel:hover { background:rgba(255,255,255,0.13); }
  .msb-dd { position:absolute; top:calc(100% + 4px); left:0; background:#1e2a3a; border:1px solid rgba(255,255,255,0.1); border-radius:8px; padding:4px; z-index:1000; min-width:180px; box-shadow:0 8px 24px rgba(0,0,0,0.4); }
  .msb-ddi { padding:8px 10px; border-radius:5px; cursor:pointer; font-size:12px; color:rgba(255,255,255,0.8); display:flex; align-items:center; gap:7px; }
  .msb-ddi:hover { background:rgba(255,255,255,0.08); color:white; }
  .msb-ddi.on { color:#fbbf24; background:rgba(251,191,36,0.08); }
  .msb-pstatus { font-size:9px; padding:1px 5px; border-radius:3px; }
  .msb-pstatus.live { background:rgba(34,197,94,0.15); color:#22c55e; }
  .msb-pstatus.draft { background:rgba(245,158,11,0.15); color:#f59e0b; }
  .msb-mo { position:fixed; inset:0; background:rgba(0,0,0,0.65); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; backdrop-filter:blur(3px); }
  .msb-mo-box { background:#161b22; border:1px solid rgba(255,255,255,0.1); border-radius:12px; width:100%; max-width:500px; max-height:70vh; display:flex; flex-direction:column; box-shadow:0 24px 60px rgba(0,0,0,0.5); }
  .msb-mo-head { padding:14px 18px; border-bottom:1px solid rgba(255,255,255,0.07); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
  .msb-mo-body { padding:10px; overflow-y:auto; flex:1; }
  .msb-vi { display:flex; align-items:center; justify-content:space-between; padding:10px 12px; border-radius:7px; margin-bottom:4px; border:1px solid rgba(255,255,255,0.05); }
  .msb-vi:hover { background:rgba(255,255,255,0.04); }
  .msb-vtime { font-size:12px; color:rgba(255,255,255,0.7); }
  .msb-vsize { font-size:10px; color:rgba(255,255,255,0.3); font-family:monospace; margin-top:2px; }
`;

let peStyleInjected = false;
function injectPEStyle() {
  if (peStyleInjected) return;
  peStyleInjected = true;
  const s = document.createElement('style');
  s.textContent = PE_CSS;
  document.head.appendChild(s);
}

// ── HTML Edit Pane ────────────────────────────────────────────────
function HTMLEditPane({ html, onChange, device }) {
  const [preview, setPreview] = useState(html);

  // Debounce iframe updates — don't re-render on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setPreview(html), 700);
    return () => clearTimeout(t);
  }, [html]);

  const iframeW = device === 'mobile' ? '390px' : device === 'tablet' ? '768px' : '100%';
  const iframeH = device === 'desktop' ? '100%' : '900px';

  return (
    <div className="pe-html">

      {/* Left — code editor */}
      <div className="pe-code-col">
        <div className="pe-col-head">
          <span>HTML + CSS</span>
          <span>{(html.length / 1024).toFixed(1)} KB</span>
        </div>
        <textarea
          className="pe-code-ta"
          value={html}
          onChange={e => onChange(e.target.value)}
          spellCheck={false}
          placeholder={'<!DOCTYPE html>\n<html lang="id">\n<head>...</head>\n<body>...</body>\n</html>'}
        />
      </div>

      {/* Right — live preview */}
      <div className="pe-preview-col">
        <div className="pe-col-head">
          <span>Live Preview</span>
          {device !== 'desktop' && <span style={{ color: '#fbbf24', fontWeight: 500 }}>{iframeW}</span>}
        </div>
        <div className="pe-preview-wrap">
          <iframe
            className="pe-preview-iframe"
            srcDoc={DOMPurify.sanitize(preview, {
              WHOLE_DOCUMENT: true,
              FORCE_BODY: false,
              ADD_TAGS: ['style', 'meta', 'link'],
              ADD_ATTR: ['name', 'content', 'rel', 'type', 'onclick', 'onmouseover', 'onmouseout'],
            })}
            sandbox="allow-same-origin allow-scripts"
            style={{ width: iframeW, height: iframeH }}
            title="Live Preview"
          />
        </div>
      </div>

    </div>
  );
}

// ── Page Editor (main) ────────────────────────────────────────────
export default function PageEditor({
  initialHTML,
  onSave,
  onClose,
  pageName,
  pages = [],
  onPageChange,
  editingPage,
  publishPage,
  unpublishPage,
  getLiveUrl,
}) {
  const [html,       setHtml]       = useState(initialHTML || '');
  const [saving,     setSaving]     = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unsaved,    setUnsaved]    = useState(false);
  const [showVer,    setShowVer]    = useState(false);
  const [versions,   setVersions]   = useState([]);
  const [showDD,     setShowDD]     = useState(false);
  const [device,     setDevice]     = useState('desktop');

  injectPEStyle();

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = async () => {
    pushVersion(editingPage?.slug, html);
    setSaving(true);
    try { await onSave(html); setUnsaved(false); } catch (_) {}
    setSaving(false);
  };

  // ── Publish ───────────────────────────────────────────────────
  const handlePublish = async () => {
    setPublishing(true);
    await handleSave();
    await publishPage?.();
    setPublishing(false);
  };

  // ── Version restore ───────────────────────────────────────────
  const handleRestore = (restoredHtml) => {
    if (!window.confirm('Restore versi ini? Perubahan belum tersimpan akan hilang.')) return;
    setHtml(restoredHtml);
    setUnsaved(true);
    setShowVer(false);
  };

  // ── Device ───────────────────────────────────────────────────
  const handleDevice = (d) => {
    setDevice(d);
  };

  const isLive   = editingPage?.status === 'published';
  const pageIcon = (slug) =>
    ({ home:'🏠', tentang:'👥', multichannel:'📡', kuponku:'🎫', blog:'📝', kontak:'📞', karir:'💼' }[slug] ?? '📄');

  return (
    <div className="pe-wrap" style={{ height: 'calc(100vh - 56px)', margin: '-24px' }}>

      {/* ── TOP BAR ────────────────────────────────────────────── */}
      <div className="pe-top">

        {/* Back */}
        <button className="msb-btn msb-btn-ghost" onClick={onClose}>
          <ArrowLeft size={13} /> Kembali
        </button>
        <div className="msb-div" />

        {/* Page selector */}
        <div style={{ position: 'relative' }}>
          <div className="msb-psel" onClick={() => setShowDD(v => !v)}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Halaman:</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{pageName}</span>
            <ChevronDown size={12} />
          </div>
          {showDD && (
            <div className="msb-dd">
              {pages.map(p => (
                <div
                  key={p.id}
                  className={`msb-ddi${p.id === editingPage?.id ? ' on' : ''}`}
                  onClick={() => { setShowDD(false); if (p.id !== editingPage?.id) onPageChange?.(p); }}
                >
                  <span>{pageIcon(p.slug)}</span>
                  <span style={{ flex: 1 }}>{p.name}</span>
                  <span className={`msb-pstatus ${p.status === 'published' ? 'live' : 'draft'}`}>
                    {p.status === 'published' ? 'Live' : 'Draft'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status */}
        <div className={isLive ? 'msb-dot-live' : 'msb-dot-draft'} />
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{isLive ? 'Live' : 'Draft'}</span>
        {unsaved && <div className="msb-dot-unsaved" title="Ada perubahan belum tersimpan" />}

        <div className="msb-spacer" />

        {/* Device */}
        {[['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]].map(([d, Icon]) => (
          <button key={d} className={`msb-ibt${device === d ? ' on' : ''}`} onClick={() => handleDevice(d)} title={d}>
            <Icon size={14} />
          </button>
        ))}

        <div className="msb-div" />

        {/* Version history */}
        <button
          className="msb-ibt"
          onClick={() => { setVersions(getVersions(editingPage?.slug)); setShowVer(true); }}
          title="Riwayat Versi"
        >
          <Clock size={14} />
        </button>

        {/* Open live */}
        {getLiveUrl && editingPage && (
          <a href={getLiveUrl(editingPage.slug)} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', display: 'flex' }}>
            <button className="msb-ibt" title="Buka halaman live"><ExternalLink size={14} /></button>
          </a>
        )}

        <div className="msb-div" />

        {/* Save */}
        <button className="msb-btn msb-btn-save" onClick={handleSave} disabled={saving || !unsaved}>
          <Save size={12} /> {saving ? 'Menyimpan...' : 'Simpan'}
        </button>

        {/* Publish / Unpublish */}
        {isLive
          ? <button className="msb-btn msb-btn-unpub" onClick={() => unpublishPage?.()}>Unpublish</button>
          : <button className="msb-btn msb-btn-pub" onClick={handlePublish} disabled={publishing}>
              {publishing ? 'Publishing...' : '✦ Publish'}
            </button>
        }
      </div>

      {/* ── TAB BAR ────────────────────────────────────────────── */}
      <div className="pe-tabs">
        <button className="pe-tab on">
          <Code size={13} /> HTML Edit
        </button>
        <span className="pe-tab-hint">Edit HTML + CSS langsung · Preview real-time di kanan</span>
      </div>

      {/* ── CONTENT ────────────────────────────────────────────── */}
      <div className="pe-content">
        <HTMLEditPane
          html={html}
          onChange={v => { setHtml(v); setUnsaved(true); }}
          device={device}
        />
      </div>

      {/* ── VERSION HISTORY MODAL ──────────────────────────────── */}
      {showVer && (
        <div className="msb-mo" onClick={e => e.target === e.currentTarget && setShowVer(false)}>
          <div className="msb-mo-box">
            <div className="msb-mo-head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Clock size={15} color="#fbbf24" />
                <span style={{ fontWeight: 700, fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>
                  Riwayat Versi — {pageName}
                </span>
              </div>
              <button className="msb-ibt" onClick={() => setShowVer(false)}><X size={13} /></button>
            </div>
            <div className="msb-mo-body">
              {versions.length === 0
                ? (
                  <div style={{ textAlign: 'center', padding: '32px 16px', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
                    Belum ada riwayat versi.<br />
                    <span style={{ fontSize: 11 }}>Versi disimpan setiap kamu menekan Simpan.</span>
                  </div>
                )
                : versions.map((v, i) => (
                  <div key={v.id || i} className="msb-vi">
                    <div>
                      <div className="msb-vtime">{i === 0 ? '⭐ ' : ''}{fmtDate(v.ts)}</div>
                      <div className="msb-vsize">{(v.html.length / 1024).toFixed(1)} KB</div>
                    </div>
                    <button
                      className="msb-btn msb-btn-ghost"
                      style={{ fontSize: 11, padding: '3px 9px' }}
                      onClick={() => handleRestore(v.html)}
                    >
                      <RotateCcw size={11} /> Restore
                    </button>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Close dropdown on outside click */}
      {showDD && <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowDD(false)} />}

    </div>
  );
}
